import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaChevronUp } from 'react-icons/fa';
import styled, { css } from 'styled-components';
import Dropdown from '../Dropdown';
import { LabelType, FilterFieldLabelProvider, OptionLabelProvider } from '../LabelProvider';
import { useProductBrowser } from '../ProductBrowserContext';
import { desktop, mobile } from '../styles';
import useBreakpoint from '../useBreakpoint';
import useId from '../useId';
var filterField = {
  field: 'sort',
  label: 'Sort'
};
var labelOptions = {
  whiteSpace: 'nowrap',
  labelType: LabelType.SORT_DROPDOWN
};
var SortDropdownButton = styled(Dropdown.Button).withConfig({
  displayName: "SortDropdown__SortDropdownButton",
  componentId: "sc-10bg7ho-0"
})(["\n  cursor: pointer;\n  font-size: 16px;\n  line-height: 1;\n  padding: 0 16px;\n  position: relative;\n  width: auto;\n  height: 48px;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  ", "\n\n  &[data-expanded='true'] {\n    background-color: #333333;\n    color: #fff;\n  }\n  ", ";\n"], desktop(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    height: 32px;\n    padding: 0 8px;\n    font-size: 14px;\n  "]))), _ref => {
  var buttonStyle = _ref.buttonStyle;
  return buttonStyle;
});
var ActiveSelectionText = styled.span.withConfig({
  displayName: "SortDropdown__ActiveSelectionText",
  componentId: "sc-10bg7ho-1"
})(["\n  color: #676767;\n  &[data-expanded='true'] {\n    color: #fff;\n  }\n  ", ";\n"], _ref2 => {
  var activeSelectionStyle = _ref2.activeSelectionStyle;
  return activeSelectionStyle;
});
var MobileLabel = styled.label.withConfig({
  displayName: "SortDropdown__MobileLabel",
  componentId: "sc-10bg7ho-2"
})(["\n  font-size: 16px;\n  ", "\n  white-space: pre-wrap;\n  ", ";\n"], desktop(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: none;\n  "]))), _ref3 => {
  var mobileLabelStyle = _ref3.mobileLabelStyle;
  return mobileLabelStyle;
});
var Chevron = styled(FaChevronUp).withConfig({
  displayName: "SortDropdown__Chevron",
  componentId: "sc-10bg7ho-3"
})(["\n  font-size: 8px;\n  margin-left: 6px;\n  vertical-align: middle;\n  ", "\n"], p => p.direction && {
  up: css(["\n        transform: rotate(0deg);\n      "]),
  down: css(["\n        transform: rotate(180deg);\n      "]),
  left: css(["\n        transform: rotate(270deg) translateX(1px);\n      "]),
  right: css(["\n        transform: rotate(90deg) translateX(-1px);\n      "])
}[p.direction]);
Chevron.propTypes = {
  direction: PropTypes.oneOf(['up', 'down', 'left', 'right'])
};
/**
 * The implementation of Dropdown.Button for sorting.
 *
 * Doesn't accept props as it receives the data it needs from the nearest DropdownContext.
 * And as such should only be used within a DropdownContext.
 */

var DropdownButton = _ref4 => {
  var activeSelectionStyle = _ref4.activeSelectionStyle,
      buttonStyle = _ref4.buttonStyle,
      _ref4$chevronIcon = _ref4.chevronIcon,
      ChevronIcon = _ref4$chevronIcon === void 0 ? Chevron : _ref4$chevronIcon,
      id = _ref4.id,
      mobileLabelStyle = _ref4.mobileLabelStyle,
      labelSeparator = _ref4.labelSeparator,
      showDefaultOption = _ref4.showDefaultOption;

  var _useBreakpoint = useBreakpoint(),
      isMobile = _useBreakpoint.isMobile;

  var _Dropdown$useDropdown = Dropdown.useDropdownContext(),
      _Dropdown$useDropdown2 = _slicedToArray(_Dropdown$useDropdown, 1),
      dropdownState = _Dropdown$useDropdown2[0];

  var filterFieldLabelCtx = FilterFieldLabelProvider.useLabels();
  var optionLabelCtx = OptionLabelProvider.useLabels();
  var isDefault = dropdownState.selectedOption.isDefault;
  var sortHasChanged = dropdownState.hasChanged;
  var showActiveOption = !isDefault || sortHasChanged || !isMobile || showDefaultOption;
  return /*#__PURE__*/React.createElement(SortDropdownButton, {
    id: id,
    "data-autotag": "sort-dropdown",
    "data-expanded": dropdownState.isOpened,
    buttonStyle: buttonStyle
  }, /*#__PURE__*/React.createElement(MobileLabel, {
    "data-expanded": dropdownState.isOpened,
    mobileLabelStyle: mobileLabelStyle
  }, filterFieldLabelCtx.renderLabel(filterField, labelOptions), (!isDefault || sortHasChanged || showDefaultOption) && labelSeparator), showActiveOption && /*#__PURE__*/React.createElement(ActiveSelectionText, {
    "data-expanded": dropdownState.isOpened,
    activeSelectionStyle: activeSelectionStyle
  }, optionLabelCtx.renderLabel(filterField, dropdownState.selectedOption, labelOptions)), /*#__PURE__*/React.createElement(ChevronIcon, {
    direction: dropdownState.isOpened ? 'up' : 'down'
  }));
};

DropdownButton.propTypes = {
  activeSelectionStyle: PropTypes.any,
  buttonStyle: PropTypes.any,
  chevronIcon: PropTypes.elementType,
  id: PropTypes.string,
  labelSeparator: PropTypes.string,
  mobileLabelStyle: PropTypes.any,
  showDefaultOption: PropTypes.bool
};
var DropdownWrapper = styled.div.withConfig({
  displayName: "SortDropdown__DropdownWrapper",
  componentId: "sc-10bg7ho-4"
})(["\n  color: #333333;\n  display: flex;\n  text-align: left;\n  position: relative;\n"]);
var ButtonListWrapper = styled.div.withConfig({
  displayName: "SortDropdown__ButtonListWrapper",
  componentId: "sc-10bg7ho-5"
})(["\n  flex-grow: 1;\n  position: relative;\n  ", "\n\n  ", ";\n"], desktop(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    flex-grow: 0;\n    display: inline-block;\n  "]))), _ref5 => {
  var buttonListWrapperStyle = _ref5.buttonListWrapperStyle;
  return buttonListWrapperStyle;
});
var DropdownList = styled(Dropdown.List).withConfig({
  displayName: "SortDropdown__DropdownList",
  componentId: "sc-10bg7ho-6"
})(["\n  background-color: #fff;\n  font-size: 14px;\n  min-width: 100%;\n  position: absolute;\n  left: 0;\n  z-index: 1;\n  ", "\n  ", ";\n"], desktop(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    font-size: 12px;\n    left: auto;\n    right: 0;\n  "]))), _ref6 => {
  var dropdownListStyle = _ref6.dropdownListStyle;
  return dropdownListStyle;
});
var DropdownListItem = styled(Dropdown.ListItem).withConfig({
  displayName: "SortDropdown__DropdownListItem",
  componentId: "sc-10bg7ho-7"
})(["\n  border-top: 1px solid #eeeeee;\n  cursor: pointer;\n  padding: 16px 8px;\n  white-space: nowrap;\n  &[aria-current='true'] {\n    background-color: #ddedff;\n  }\n  ", "\n  ", ";\n"], desktop(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    padding: 8px;\n    &[data-selected='true'] {\n      color: #676767;\n    }\n  "]))), _ref7 => {
  var dropdownListItemStyle = _ref7.dropdownListItemStyle;
  return dropdownListItemStyle;
});
var DesktopLabel = styled.label.withConfig({
  displayName: "SortDropdown__DesktopLabel",
  componentId: "sc-10bg7ho-8"
})(["\n  padding: 9px 8px 9px 0;\n  font-size: 14px;\n  line-height: 1;\n  ", "\n\n  ", ";\n"], mobile(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    display: none;\n  "]))), _ref8 => {
  var desktopLabelStyle = _ref8.desktopLabelStyle;
  return desktopLabelStyle;
});
export var SortDropdown = _ref9 => {
  var id = _ref9.id,
      valueFromProps = _ref9.value,
      optionsFromProps = _ref9.options,
      buttonStyle = _ref9.buttonStyle,
      chevronIcon = _ref9.chevronIcon,
      onChange = _ref9.onChange,
      activeSelectionStyle = _ref9.activeSelectionStyle,
      dropdownListStyle = _ref9.dropdownListStyle,
      buttonListWrapperStyle = _ref9.buttonListWrapperStyle,
      dropdownListItemStyle = _ref9.dropdownListItemStyle,
      desktopLabelStyle = _ref9.desktopLabelStyle,
      mobileLabelStyle = _ref9.mobileLabelStyle,
      onOpen = _ref9.onOpen,
      onClose = _ref9.onClose,
      _ref9$labelSeparator = _ref9.labelSeparator,
      labelSeparator = _ref9$labelSeparator === void 0 ? ': ' : _ref9$labelSeparator,
      _ref9$showDefaultOpti = _ref9.showDefaultOption,
      showDefaultOption = _ref9$showDefaultOpti === void 0 ? false : _ref9$showDefaultOpti,
      rest = _objectWithoutProperties(_ref9, ["id", "value", "options", "buttonStyle", "chevronIcon", "onChange", "activeSelectionStyle", "dropdownListStyle", "buttonListWrapperStyle", "dropdownListItemStyle", "desktopLabelStyle", "mobileLabelStyle", "onOpen", "onClose", "labelSeparator", "showDefaultOption"]);

  var productBrowserCtx = useProductBrowser();
  var filterFieldLabelCtx = FilterFieldLabelProvider.useLabels();
  var optionLabelCtx = OptionLabelProvider.useLabels();
  var autoId = useId();
  id = id || autoId;
  var value = useMemo(() => {
    if (valueFromProps) {
      return valueFromProps;
    } else if (productBrowserCtx && productBrowserCtx.sort) {
      return productBrowserCtx.sort.value;
    }
  }, [productBrowserCtx, valueFromProps]);
  var options = useMemo(() => {
    if (optionsFromProps) {
      return optionsFromProps;
    } else if (productBrowserCtx && productBrowserCtx.sortOptions) {
      return productBrowserCtx.sortOptions;
    }
  }, [optionsFromProps, productBrowserCtx]);
  var handleSortChange = useCallback(option => {
    if (onChange) {
      onChange(option);
    } else if (productBrowserCtx && productBrowserCtx.setSort) {
      productBrowserCtx.setSort(option);
    }
  }, [onChange, productBrowserCtx]);
  return /*#__PURE__*/React.createElement(DropdownWrapper, rest, /*#__PURE__*/React.createElement(Dropdown, {
    value: value,
    options: options,
    onChange: handleSortChange,
    onOpen: onOpen,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(DesktopLabel, {
    desktopLabelStyle: desktopLabelStyle,
    htmlFor: id
  }, filterFieldLabelCtx.renderLabel(filterField, labelOptions)), /*#__PURE__*/React.createElement(ButtonListWrapper, {
    buttonListWrapperStyle: buttonListWrapperStyle
  }, /*#__PURE__*/React.createElement(DropdownButton, {
    id: id,
    buttonStyle: buttonStyle,
    chevronIcon: chevronIcon,
    activeSelectionStyle: activeSelectionStyle,
    mobileLabelStyle: mobileLabelStyle,
    labelSeparator: labelSeparator,
    showDefaultOption: showDefaultOption
  }), /*#__PURE__*/React.createElement(DropdownList, {
    dropdownListStyle: dropdownListStyle
  }, options.map(option => {
    return /*#__PURE__*/React.createElement(DropdownListItem, {
      value: option.value,
      key: option.value,
      "data-autotag": "sort-by-".concat(option.value),
      "data-selected": option.value === value,
      dropdownListItemStyle: dropdownListItemStyle
    }, optionLabelCtx.renderLabel(filterField, option, labelOptions));
  })))));
};
SortDropdown.Chevron = Chevron;
SortDropdown.DropdownButton = DropdownButton;
SortDropdown.DesktopLabel = DesktopLabel;
SortDropdown.DropdownList = DropdownList;
SortDropdown.DropdownListItem = DropdownListItem;
export var individualSortOption = PropTypes.shape({
  value: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
  sort: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc'])
  }))])
});
export var sortOptionPropType = PropTypes.arrayOf(individualSortOption);
SortDropdown.propTypes = {
  activeSelectionStyle: PropTypes.any,
  buttonListWrapperStyle: PropTypes.any,
  buttonStyle: PropTypes.any,
  chevronIcon: PropTypes.elementType,
  desktopLabelStyle: PropTypes.any,
  dropdownListItemStyle: PropTypes.any,
  dropdownListStyle: PropTypes.any,
  id: PropTypes.string,

  /**
   * Flag to disable showing the coma after the mobile label on selection
   */
  labelSeparator: PropTypes.string,
  mobileLabelStyle: PropTypes.any,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,

  /**
   * Array of available sort options
   */
  options: sortOptionPropType,

  /**
   * Use showDefaultOption to display default Option in mobile mode.
   */
  showDefaultOption: PropTypes.bool,

  /**
   * Currently selected value. If none provided the first option's value
   * will be used.
   */
  value: PropTypes.string
};
export default SortDropdown;
SortDropdown.ChevronIcon = Chevron;